import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    TextField,
    InputAdornment,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Box,
    Button,
    Typography,
    styled,
    Alert,
    CircularProgress
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from './LoadingIndicator';
import CustomAlert from './CustomAlert';

const SearchTextField = styled(TextField)`
  margin-bottom: 16px;
  width: 100%;
  
  .MuiOutlinedInput-root {
    border-radius: 12px;
    transition: all 0.2s ease;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.01);
    }
    
    &.Mui-focused {
      background-color: white;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
`;

const DialogHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid ${props => props.theme.palette.divider};
`;

const StyledDialogTitle = styled(Typography)`
  font-size: 1.25rem;
  font-weight: 600;
`;

const SearchContainer = styled(Box)`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const StyledTableCell = styled(TableCell)`
  text-align: center;
  padding: 12px 8px;
`;

const HeaderCell = styled(StyledTableCell)`
  background-color: ${props => props.theme.palette.primary.main};
  color: #fff;
  font-weight: 600;
  border-right: 1px solid ${props => props.theme.palette.primary.dark};
  
  &:last-child {
    border-right: none;
  }
`;

const BodyCell = styled(StyledTableCell)`
  border-right: 1px solid ${props => props.theme.palette.divider};
  
  &:last-child {
    border-right: none;
  }
`;

const StyledTableRow = styled(TableRow)`
  &:hover {
    background-color: ${props => props.theme.palette.action.hover};
    cursor: pointer;
  }
`;

const SearchDialog = ({
    open,
    onClose,
    onSelect,
    initialSearchResult,
    initialValue = '',
    allData,
    title,
    placeholder,
    searchKeys = ['name', 'code'],
    columns = [],
    renderRow,
    customFilter,
    listTitle,
    isLoading = false,
    useInfiniteScroll = false,
    autoSelectSingle = false,
    requireSearchTerm = false
}) => {
    const { t } = useTranslation();
    const searchInputRef = useRef(null);
    const dialogRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [error, setError] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [visibleCount, setVisibleCount] = useState(50);
    const [emptyTermAlert, setEmptyTermAlert] = useState(false);

    useEffect(() => {
        setVisibleCount(50);
    }, [filteredData]);

    const handleListScroll = useCallback((e) => {
        if (!useInfiniteScroll) return;
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
            setVisibleCount((prev) => prev + 50);
        }
    }, [useInfiniteScroll]);

    const visibleRows = useInfiniteScroll
        ? filteredData.slice(0, visibleCount)
        : filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleDialogKeyDown = (e) => {
        if (e.key === 'F4') {
            e.preventDefault();
            e.stopPropagation();
            handleSearch();
            searchInputRef.current?.focus();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleClose();
        }
    };

    const clearFocus = useCallback(() => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        searchInputRef.current?.blur();
        dialogRef.current?.blur();
        document.getElementById('root')?.removeAttribute('aria-hidden');
        document.body.style.overflow = 'unset';
    }, []);

    useEffect(() => {
        if (open) {
            // 초기 검색어가 있다면 설정하고 자동으로 검색 실행
            if (initialValue && initialValue.trim() !== '' && allData && allData.length > 0) {
                setSearchTerm(initialValue);
                // 초기 검색어로 자동 필터링
                setTimeout(() => {
                    const filtered = customFilter
                        ? customFilter(allData, initialValue)
                        : allData.filter(item => {
                            const searchTermLower = initialValue.toLowerCase();
                            return searchKeys.some(key => {
                                const value = item[key]?.toString().toLowerCase() || '';
                                return value.includes(searchTermLower);
                            });
                        });
                    setFilteredData(filtered);
                }, 100);
            } else {
                setSearchTerm('');
                // 검색어가 없으면 전체 데이터를 표시
                if (allData && allData.length > 0) {
                    setFilteredData(allData);
                } else {
                    setFilteredData(initialSearchResult || []);
                }
            }
            setPage(0);
            setError(null);
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [open, initialSearchResult, initialValue, allData, customFilter, searchKeys]);

    const handleSearch = async () => {
        if (!open) return;

        if (requireSearchTerm && !searchTerm.trim()) {
            setEmptyTermAlert(true);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await new Promise(resolve => setTimeout(resolve, 500));

            // allData가 없거나 빈 배열인 경우 빈 결과 반환
            if (!allData || allData.length === 0) {
                setFilteredData([]);
                setPage(0);
                return;
            }

            const filtered = !searchTerm.trim()
                ? allData
                : customFilter
                    ? customFilter(allData, searchTerm)
                    : allData.filter(item => {
                        const searchTermLower = searchTerm.toLowerCase();
                        return searchKeys.some(key => {
                            const value = item[key]?.toString().toLowerCase() || '';
                            return value.includes(searchTermLower);
                        });
                    });

            setFilteredData(filtered);
            setPage(0);

            if (autoSelectSingle && filtered.length === 1) {
                handleRowClick(filtered[0]);
            }
        } catch (error) {
            setError(t('error.search'));
            setFilteredData([]);
        } finally {
            setLoading(false);
            searchInputRef.current?.focus();
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    const handleRowClick = useCallback((item) => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        if (dialogRef.current) {
            const focusableElements = dialogRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            Array.from(focusableElements).forEach(el => {
                if (el instanceof HTMLElement) el.tabIndex = -1;
            });
        }

        onSelect(item);

        setTimeout(() => {
            document.getElementById('root')?.removeAttribute('aria-hidden');
            onClose();
        }, 10);
    }, [onSelect, onClose]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleClose = useCallback(() => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        setTimeout(() => {
            document.getElementById('root')?.removeAttribute('aria-hidden');
            document.body.style.overflow = 'unset';
            clearFocus();
            onClose();
        }, 10);
    }, [onClose, clearFocus]);

    return (
        <Dialog
            ref={dialogRef}
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            disableScrollLock
            closeAfterTransition
            aria-labelledby="search-dialog-title"
            onKeyDown={handleDialogKeyDown}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    outline: 'none'
                }
            }}
            sx={{
                '& .MuiDialog-container': {
                    '& .MuiPaper-root': {
                        width: '100%'
                    }
                }
            }}
        >
            <LoadingIndicator isLoading={loading} message={t('common.searching')} />

            <CustomAlert
                open={emptyTermAlert}
                title={t('error.title')}
                message={t('common.enterSearchTerm', '검색어를 입력해주세요.')}
                severity="warning"
                isConfirm
                onConfirm={() => { setEmptyTermAlert(false); searchInputRef.current?.focus(); }}
                onClose={() => setEmptyTermAlert(false)}
            />

            <DialogHeader>
                <StyledDialogTitle id="search-dialog-title" variant="h6">
                    {title || t('common.search')}
                </StyledDialogTitle>
                <IconButton onClick={handleClose} size="large" aria-label={t('common.close')}>
                    <CloseIcon />
                </IconButton>
            </DialogHeader>

            <DialogContent sx={{ p: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <SearchContainer>
                    <SearchTextField
                        inputRef={searchInputRef}
                        id="search-field"
                        placeholder={placeholder || t('common.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        autoComplete="off"
                        aria-label={placeholder || t('common.searchPlaceholder')}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={() => {
                            handleSearch();
                            searchInputRef.current?.focus();
                        }}
                        startIcon={<SearchIcon />}
                        sx={{
                            height: '56px',
                            borderRadius: '8px',
                            whiteSpace: 'nowrap',
                            minWidth: 'auto',
                            px: 2
                        }}
                        aria-label={t('common.search')}
                    >
                        {t('common.search')} [F4]
                    </Button>
                </SearchContainer>

                <TableContainer
                    component={Paper}
                    onScroll={handleListScroll}
                    sx={{
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        ...(useInfiniteScroll ? { maxHeight: 420, overflow: 'auto' } : {}),
                        '& .MuiTableRow-root:nth-of-type(odd)': {
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        }
                    }}
                >
                    <Table stickyHeader={useInfiniteScroll} aria-label={listTitle || t('common.list')}>
                        <TableHead>
                            <TableRow>
                                {columns.map((column, index) => (
                                    <HeaderCell key={index}>{column.label}</HeaderCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center">
                                        <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                            <CircularProgress size={40} />
                                            <Typography variant="body2" color="text.secondary">
                                                {t('common.loading')}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : loading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center">
                                        <Typography sx={{ py: 2 }}>{t('common.searching')}</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : filteredData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center">
                                        <Typography sx={{ py: 2 }}>{t('common.noData')}</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                visibleRows
                                    .map((item) => renderRow ? (
                                        renderRow(item, StyledTableRow, BodyCell, handleRowClick)
                                    ) : (
                                        <StyledTableRow
                                            key={item.id}
                                            onClick={() => handleRowClick(item)}
                                        >
                                            {columns.map((column, index) => (
                                                <BodyCell key={index}>
                                                    {item[column.key] || '-'}
                                                </BodyCell>
                                            ))}
                                        </StyledTableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {!useInfiniteScroll && filteredData.length > 0 && (
                    <TablePagination
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={t('common.rowsPerPage')}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default SearchDialog;